import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IMusicProductDigitalSettlementInfoMutation } from './types/mutation';
import {
  IMusicProductDigitalSettlementInfoResponse,
  ISettlementInfoTime,
} from './types/query';

export const usePostMusicProductDigitalSettlementInfo = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostMusicProductDigitalSettlementInfo'
  );
  return useMutation<
    IMusicProductDigitalSettlementInfoResponse,
    DefaultError,
    IMusicProductDigitalSettlementInfoMutation
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('music-product-digital.contracts.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'MusicProductDigital/SettlementInfo',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostMusicProductDigitalSettlementInfo',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('music-product-digital.contracts.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetMusicProductDigitalContractFuture = (
  DigitalMusicProductId: string
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<
    ISettlementInfoTime<IMusicProductDigitalSettlementInfoResponse>,
    DefaultError
  >({
    queryKey: ['music-product-digital-contract-future', DigitalMusicProductId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/Contract/Future`,
        params: { DigitalMusicProductId },
      });

      return responseHandler(response, {
        groupName: `useGetMusicProductDigitalContractFuture - ${DigitalMusicProductId}`,
      });
    },
  });
};

export const useGetMusicProductDigitalContractHistory = (
  DigitalMusicProductId: string
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<
    ISettlementInfoTime<IMusicProductDigitalSettlementInfoResponse>,
    DefaultError
  >({
    queryKey: ['music-product-digital-contract-history', DigitalMusicProductId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `MusicProductDigital/Contract/History`,
        params: { DigitalMusicProductId },
      });

      return responseHandler(response, {
        groupName: `useGetMusicProductDigitalContractHistory - ${DigitalMusicProductId}`,
      });
    },
  });
};

export const useClearContractFuture = <TBody extends {}>(
  body: TBody,
  endpointPrefix = 'MusicProductDigital'
) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useClearContractFuture');
  return useMutation<unknown, DefaultError, undefined>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('contract-template.contract.what-future'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: `${endpointPrefix}/Contract/ClearFuture`,
        body,
      });

      return responseHandler(response, {
        groupName: `useClearContractFuture - ${endpointPrefix}`,
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('contract-template.contract.what-future'),
        })
      );
    },
    onError: dismiss,
  });
};
